import { NotificationSource } from './notification-source'
import type { NotificationHandler } from './notification-handler'
import { NotificationHandlerEventType } from './notification-handler-event'
import type { NotificationHandlerFactory } from './notification-handler-factory'
import { WebSocketNotificationHandlerFactory } from './websocket-notification-handler'
import { destroyChannelInfo, getChannelInfo, updateChannelInfoLifeTime } from './channel-info-service'
import type { NotificationChannel } from './model'
import type { ChannelInfo, NotificationChannelEvent } from '../notification-channel/models'
import { getShellLogger } from '../../common/logger'
import { NodeEventEmitter } from '../namespaces'
import { Timer } from '../notification-channel/helpers'

export class NotificationChannelImpl implements NotificationChannel {
  private notificationSource: NotificationSource = NotificationSource.WEBSOCKET
  private notificationHandler: NotificationHandler | null = null
  private emitter: NodeEventEmitter

  constructor(private handlerFactory: NotificationHandlerFactory, private externalUserKey: string) {
    this.emitter = new NodeEventEmitter()
    this.emitter.addListener(NotificationHandlerEventType.UPDATE_CHANNEL_LIFETIME, this.handleUpdateChannelLifetime)
    this.emitter.addListener(NotificationHandlerEventType.RESTART_REQUIRED, this.handleRestartRequiredEvent)
  }

  public start() {
    this.initializeHandler()
      .then(() => {
        this.notificationHandler?.connect()
      })
      .catch(() => {
        new Timer(this.internalRestart.bind(this), 4000)
      })
  }

  public get channelInfo() {
    return this.notificationHandler?.channelInfo
  }

  public get isConnected(): boolean {
    return !!this.notificationHandler?.isConnected
  }

  public get id() {
    return this.notificationHandler?.channelInfo.channelId ?? ''
  }

  public setNotificationSource(notificationSource: NotificationSource) {
    this.clearHandler()
    this.notificationSource = notificationSource
    switch (this.notificationSource) {
      case NotificationSource.WEBSOCKET:
        this.handlerFactory = WebSocketNotificationHandlerFactory
        break
      case NotificationSource.PUSH:
        throw new Error('push notification are not supported yet')
    }
    this.initializeHandler()
  }

  public subscribe<T>(eventListener: (event: NotificationChannelEvent<T>) => void): void {
    this.emitter.addListener(NotificationHandlerEventType.MESSAGE, eventListener)
  }

  public unsubscribe<T>(eventListener: (event: NotificationChannelEvent<T>) => void): void {
    this.emitter.removeListener(NotificationHandlerEventType.MESSAGE, eventListener)
  }

  private async initializeHandler() {
    const channelInfo = await getChannelInfo(this.externalUserKey)
    this.notificationHandler = this.handlerFactory({
      channelInfo,
      emitter: this.emitter,
    })

    window.addEventListener('online', this.handleWindowOnlineEvent)
    window.addEventListener('offline', this.handleWindowOfflineEvent)
  }

  private internalRestart() {
    this.clearHandler()
    this.destroyChannelInfo()
    this.start()
  }

  private clearHandler() {
    this.notificationHandler?.delete()
    window.removeEventListener('online', this.handleWindowOnlineEvent)
    window.removeEventListener('offline', this.handleWindowOfflineEvent)
  }

  private handleWindowOnlineEvent = () => {
    this.notificationHandler?.resume()
    this.emitter.emit(NotificationHandlerEventType.OPEN)
    getShellLogger().log('WebSocket:You are now connected to the network')
  }

  private handleWindowOfflineEvent = () => {
    this.notificationHandler?.pause()
    this.emitter.emit(NotificationHandlerEventType.CLOSE, {
      errorCode: 'ERR_INTERNET_DISCONNECTED',
      message: 'No Internet',
    })
  }

  private handleRestartRequiredEvent = (event: Event) => {
    getShellLogger().error('WebSocket:Restart Required', event)
    this.internalRestart()
  }

  private handleUpdateChannelLifetime = () => {
    updateChannelInfoLifeTime(this.externalUserKey, this.id)
      .then((channelInfo: ChannelInfo) => {
        this.notificationHandler?.updateChannelInfo(channelInfo)
        getShellLogger().log('WebSocket:Lifetime updated')
      })
      .catch((error: Error) => {
        getShellLogger().error('WebSocket:Lifetime failed to update', error)
        this.internalRestart()
      })
  }

  private destroyChannelInfo() {
    if (this.channelInfo) {
      destroyChannelInfo(this.externalUserKey, this.channelInfo.channelId).catch(error => {
        getShellLogger().error('WebSocket:Failed to remove previous notification channel', error)
      })
    }
  }
}
